import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../../redux/storeCartSlice";
import { fetchAllCommercePartners } from "../../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../../redux/storeProductRatingSlice";
import { logout } from "../../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../../GLOBAL/CurrencyContext";
import appLogo from "../../../../assets/app/logo.png";

/* ⭐ CATEGORY SOURCE — the same collection StoreProducts saves with.
   This page is the Beauty department and nothing else: the swatch bar
   carries Beauty's own categories, and everything below drills
   category → subCategory → productType → product. */
import {
  DEPARTMENTS,
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, X, ChevronRight, ChevronDown, Plus, Minus, Check,
  Heart, User, ShoppingBag, MapPin, Globe, AlertTriangle, LogOut,
  Home, Sparkles, Star, LayoutGrid, Package, Droplet, Sliders,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE — resolved by name first so a renamed or re-numbered
   department doesn't silently empty the page.
═══════════════════════════════════════════════════════════ */
const BEAUTY_DEPT_ID: string =
  DEPARTMENTS.find((d) => /beauty|cosmetic|makeup|skincare/i.test(d.name))?.id ?? "04";

/* ═══════════════════════════════════════════════════════════
   DESIGN — a lit vanity counter.

   Blush paper, glossy pills, soft shadows that fall pink rather
   than grey. Hot pink is the house colour here and it carries the
   chrome — the swatch bar, the bag badge, the focus ring. Product
   accents still come from the seller's own brand colour, so a wall
   of products reads as a wall of labels rather than one big pink.
═══════════════════════════════════════════════════════════ */
const B = {
  blush:     "#FFF4F9",   // page
  blushSoft: "#FFFAFC",   // sheets & modals
  panel:     "#FFFFFF",   // cards
  panel2:    "#FFEFF6",
  raise:     "#FFE1EE",
  line:      "#FBD9E7",
  lineSoft:  "#FDE9F1",
  ink:       "#2B0F1E",
  inkSoft:   "#5A2C43",
  muted:     "#8E6377",
  faint:     "#BE94A6",
  ok:        "#3FA07A",
  warn:      "#E0952B",
  danger:    "#E23A6A",
};

/* page chrome */
const HOT_PINK = "#FF1F8E";
const PINK_DEEP = "#D6006E";
const GLOSS = `linear-gradient(180deg, #FF63B4 0%, ${HOT_PINK} 54%, ${PINK_DEEP} 100%)`;

/* the seller's colour carries every product-level accent */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || HOT_PINK;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE — ancestry always resolved by walking parentId,
   never by string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Path with the department stripped — everything here is Beauty. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).filter((cid) => cid !== BEAUTY_DEPT_ID).map(catName).join(" / ");

/** Beauty's own categories — the swatch bar's dabs. */
const BEAUTY_CATEGORIES: CategoryItem[] = childrenOf(BEAUTY_DEPT_ID);

/* a soft pink family so each dab reads as its own shade */
const SHADE_TINTS = [
  "#FF1F8E", "#FF6FA8", "#E4468C", "#FF9AB8", "#C82D77",
  "#FF7E9B", "#D9569C", "#FFB0C8", "#B8246B", "#FF4FA3",
  "#E86FA0", "#FF89C1",
];
const shadeFor = (catId: string) => {
  const idx = BEAUTY_CATEGORIES.findIndex((c) => c.id === catId);
  return SHADE_TINTS[idx >= 0 ? idx % SHADE_TINTS.length : 0];
};

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — the bridge StoreProducts already uses. On beauty
   these are shade, finish and volume, so they get billing on the
   card rather than being buried in the modal.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain].reverse().find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const key = String(categoryId);
  const hit = attrCache.get(key);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [toAttributeCategoryId(categoryId), ...chain.flatMap((c) => [`0${c}`, c])].filter(Boolean);

  let found: any[] = [];
  for (const k of keys) {
    try {
      const defs = attributesForCategory(k as any);
      if (defs && defs.length) { found = defs; break; }
    } catch { /* unknown key — keep walking up */ }
  }
  attrCache.set(key, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

const isVariantAttr = (attr: any) =>
  (attr?.inputType === "select" || !attr?.inputType) &&
  Array.isArray(attr?.options) && attr.options.length > 0;

const variantAttrsForItem = (item: any) => resolveAttributes(item?.categoryId).filter(isVariantAttr);
const hasVariants = (item: any) => variantAttrsForItem(item).length > 0;

interface Detail { name: string; value: string }
/** Only what the seller filled in — the back of the box. */
const detailsOf = (item: any): Detail[] => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .filter((def: any) => !isEmptyValue(saved[String(def.id)]))
    .map((def: any) => ({ name: def.name, value: resolveAttrValue(def, saved[String(def.id)]) }))
    .filter((d) => d.value);
};

/** Saved shades, as swatches. */
interface Swatch { id: string; name: string; hex?: string }
const shadesOf = (item: any): Swatch[] => {
  const saved = item?.attributes ?? {};
  const out: Swatch[] = [];
  resolveAttributes(item?.categoryId)
    .filter((def: any) => def.inputType === "color" && !isEmptyValue(saved[String(def.id)]))
    .forEach((def: any) => {
      const raw = saved[String(def.id)];
      (Array.isArray(raw) ? raw : [raw]).forEach((optId: any) => {
        const o = def.options?.find((x: any) => String(x.id) === String(optId));
        if (o) out.push({ id: String(o.id), name: o.name || o.standard || String(o.id), hex: o.hex });
      });
    });
  return out;
};

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const isOutOfStock = (item: any) => item?.available === false || (item?.stock ?? 0) <= 0;

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null =>
  country ? COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null : null;
const isLocalTier = (t: number) => t <= 1;
const isGlobalTier = (t: number) => t >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-thumb { background: ${B.raise}; border-radius: 99px; }
input, button, select { font-family: inherit; }
.bs :focus-visible { outline: 2px solid ${HOT_PINK}; outline-offset: 2px; border-radius: 8px; }

@keyframes bsIn { from { opacity:0; transform: translateY(12px);} to { opacity:1; transform:none; } }
@keyframes bsShimmer { 0% { transform: translateX(-120%); } 60%,100% { transform: translateX(260%); } }
@keyframes bsTwinkle { 0%,100% { opacity:.35; transform:scale(.85);} 50% { opacity:1; transform:scale(1);} }
@keyframes bsSpin { to { transform: rotate(360deg); } }

/* blush paper with a faint confetti of dots */
.bs {
  min-height:100vh; background:${B.blush}; color:${B.ink};
  font-family:'DM Sans', sans-serif; padding-bottom:96px;
  background-image: radial-gradient(${B.lineSoft} 1.4px, transparent 1.4px);
  background-size: 26px 26px;
}
.bs-serif { font-family:'Fraunces', Georgia, serif; font-weight:600; letter-spacing:-.015em; }
.bs-caps { font-size:10px; letter-spacing:.2em; text-transform:uppercase; font-weight:600; }
.bs-num { font-variant-numeric: tabular-nums; }

/* ── counter rail ── */
.bs-top { position:sticky; top:0; z-index:200; background:rgba(255,244,249,.9);
          backdrop-filter:blur(14px); border-bottom:1px solid ${B.line}; }
.bs-hbtn { display:flex; align-items:center; gap:6px; background:${B.panel};
  border:1px solid ${B.line}; color:${B.inkSoft}; font-size:12px; font-weight:500;
  padding:9px 14px; border-radius:999px; cursor:pointer; flex-shrink:0; white-space:nowrap;
  transition: border-color .16s, color .16s, box-shadow .16s; }
.bs-hbtn:hover { border-color:${HOT_PINK}; color:${B.ink}; box-shadow:0 3px 12px rgba(255,31,142,.14); }

.bs-search { display:flex; align-items:center; flex:1; min-width:0; background:${B.panel};
  border:1px solid ${B.line}; border-radius:999px; overflow:hidden; transition:border-color .16s, box-shadow .16s; }
.bs-search:focus-within { border-color:${HOT_PINK}; box-shadow:0 0 0 3px rgba(255,31,142,.10); }
.bs-search input { flex:1; min-width:0; background:none; border:none; outline:none;
  padding:10px 12px; font-size:13px; color:${B.ink}; }
.bs-search input::placeholder { color:${B.faint}; }

/* ── 💄 THE SWATCH BAR — categories as lipstick dabs ── */
.bs-swatches { display:flex; gap:16px; overflow-x:auto; scrollbar-width:none; padding:6px 2px 14px; }
.bs-swatch { flex-shrink:0; background:none; border:none; cursor:pointer; padding:0;
             display:flex; flex-direction:column; align-items:center; gap:7px; width:74px; }
.bs-dab { position:relative; width:60px; height:46px; overflow:hidden;
  border-radius: 58% 42% 52% 48% / 46% 58% 42% 54%;
  box-shadow: inset 0 -5px 10px rgba(0,0,0,.16), 0 5px 14px rgba(214,0,110,.18);
  transition: transform .26s cubic-bezier(.34,1.5,.64,1), box-shadow .26s; }
.bs-dab::after { content:''; position:absolute; top:6px; left:9px; width:20px; height:9px;
  border-radius:50%; background:rgba(255,255,255,.55); filter:blur(1.5px); }
.bs-dab i { position:absolute; inset:0; display:block; width:32%;
  background:linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); }
.bs-swatch:hover .bs-dab { transform: rotate(-5deg) scale(1.08); }
.bs-swatch.on .bs-dab { transform: scale(1.14);
  box-shadow: inset 0 -5px 10px rgba(0,0,0,.16), 0 0 0 3px ${B.blush}, 0 0 0 5px ${HOT_PINK}, 0 8px 18px rgba(214,0,110,.28); }
.bs-swatch.on .bs-dab i { animation: bsShimmer 2.6s ease-in-out infinite; }
.bs-swatch span { font-size:10.5px; font-weight:500; color:${B.muted}; text-align:center;
  line-height:1.25; transition:color .18s; }
.bs-swatch:hover span { color:${B.ink}; }
.bs-swatch.on span { color:${B.ink}; font-weight:700; }

/* ── product card ── */
.bs-card { background:${B.panel}; border:1px solid ${B.line}; border-radius:20px;
  overflow:hidden; cursor:pointer; display:flex; flex-direction:column;
  animation: bsIn .28s ease both; transition: border-color .2s, transform .2s, box-shadow .2s; }
.bs-card:hover { transform: translateY(-5px); }
.bs-shot { position:relative; width:100%; aspect-ratio:1/1;
  background: radial-gradient(120% 95% at 50% 10%, ${B.panel2} 0%, ${B.panel} 72%);
  display:flex; align-items:center; justify-content:center; overflow:hidden; }
.bs-shot img { width:80%; height:80%; object-fit:contain;
  filter: drop-shadow(0 14px 20px rgba(214,0,110,.16)); transition: transform .34s; }
.bs-card:hover .bs-shot img { transform: scale(1.06) rotate(-1.5deg); }

.bs-chip { display:inline-flex; align-items:center; gap:4px; font-size:9.5px; font-weight:500;
  color:${B.muted}; background:${B.panel2}; border:1px solid ${B.lineSoft};
  border-radius:999px; padding:3px 9px; white-space:nowrap; }
.bs-chip b { color:${B.inkSoft}; font-weight:700; }

.bs-flag { position:absolute; top:10px; font-size:9.5px; font-weight:700; letter-spacing:.05em;
  padding:4px 10px; border-radius:999px; }

.bs-wish { position:absolute; top:9px; right:9px; width:29px; height:29px; border-radius:50%;
  background:rgba(255,255,255,.92); border:1px solid ${B.line}; display:flex;
  align-items:center; justify-content:center; cursor:pointer; transition:border-color .18s; }
.bs-wish:hover { border-color:${HOT_PINK}; }

.bs-shade { width:15px; height:15px; border-radius:50%; flex-shrink:0;
  border:1.5px solid #fff; box-shadow:0 0 0 1px ${B.line}, inset 0 -2px 3px rgba(0,0,0,.18); }

.bs-add { width:100%; height:42px; border-radius:999px; border:1px solid; background:transparent;
  display:flex; align-items:center; justify-content:center; gap:7px; cursor:pointer;
  font-size:11.5px; font-weight:700; letter-spacing:.08em; transition: background .18s, color .18s; }

.bs-step { display:flex; align-items:center; justify-content:space-between; height:42px;
  border:1px solid; border-radius:999px; padding:0 5px; }
.bs-step button { width:30px; height:30px; border-radius:50%; border:none; cursor:pointer;
  display:flex; align-items:center; justify-content:center; }

.bs-gloss { position:relative; overflow:hidden; }
.bs-gloss::after { content:''; position:absolute; top:0; left:0; right:0; height:45%;
  background:linear-gradient(180deg, rgba(255,255,255,.28), transparent); pointer-events:none; }

.bs-panel { background:${B.panel}; border:1px solid ${B.line}; border-radius:18px; }
.bs-node { display:flex; align-items:center; gap:9px; padding:9px 11px; border-radius:11px;
  cursor:pointer; font-size:13px; color:${B.inkSoft}; transition: background .14s, color .14s; }
.bs-node:hover { background:${B.panel2}; color:${B.ink}; }
.bs-node.on { background:${B.raise}; color:${B.ink}; font-weight:600; }

.bs-tile { background:${B.panel}; border:1px solid ${B.line}; border-radius:16px;
  padding:13px 10px; cursor:pointer; display:flex; flex-direction:column; align-items:center;
  gap:8px; text-align:center; transition: border-color .16s, transform .16s, box-shadow .16s; }
.bs-tile:hover { border-color:${HOT_PINK}; transform:translateY(-2px); box-shadow:0 6px 18px rgba(255,31,142,.12); }
.bs-tile.on { border-color:${HOT_PINK}; background:${B.panel2}; }

.bs-tab { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px;
  font-weight:600; cursor:pointer; color:${B.faint}; padding:4px 10px; background:none; border:none; }
.bs-tab.on { color:${HOT_PINK}; }

@media (prefers-reduced-motion: reduce) {
  .bs-swatch.on .bs-dab i, .bs-card { animation:none !important; }
  .bs-dab, .bs-shot img, .bs-card, .bs-tile { transition:none; }
}
`;

const S: Record<string, React.CSSProperties> = {
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(228px,1fr))", gap: 18 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  tabs: {
    position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 62,
    background: "rgba(255,244,249,.96)", backdropFilter: "blur(12px)",
    borderTop: `1px solid ${B.line}`, display: "flex",
    justifyContent: "space-around", alignItems: "center", zIndex: 150,
  },
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const BeautyStore: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const filteredListings = useSelector((s: RootState) => s.storeProducts.filteredProducts);
  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector((s: RootState) => (s as any).storeProductRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const [activeCat, setActiveCat] = useState<string>(BEAUTY_CATEGORIES[0]?.id ?? BEAUTY_DEPT_ID);
  const cat = useMemo(
    () => BEAUTY_CATEGORIES.find((c) => c.id === activeCat) ?? BEAUTY_CATEGORIES[0],
    [activeCat]
  );
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [narrowOpen, setNarrowOpen] = useState(true);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [qty, setQty] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const bCity = p.address?.city?.toLowerCase().trim();
      const bCountry = p.address?.country?.toLowerCase().trim();
      const bCont = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && bCity && bCity === deviceCity) tier = 0;
      else if (deviceCountry && bCountry && bCountry === deviceCountry) tier = 1;
      else if (deviceContinent && bCont && bCont === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country) as any);
                }
              });
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName })
        .then((r) => { r.onchange = () => { if (r.state === "granted") detect(); }; })
        .catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  useEffect(() => {
    if (!detailItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailItem]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSub(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveSub(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string) => {
    setActiveCat(id); setActiveSub(null); setActiveDeep(null); setOpenNodes(new Set());
  };
  const handleSubSelect = (id: string) => { setActiveSub((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const openDetail = (item: any) => { setDetailItem(item); setQty(1); setSelectedVariants({}); };
  const closeDetail = () => setDetailItem(null);

  const handleAdd = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (hasVariants(item)) { openDetail(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;
    const defs = variantAttrsForItem(detailItem);
    if (defs.some((a: any) => selectedVariants[String(a.id)] == null)) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart({
      listingId: detailItem.listingId, name: detailItem.name!, rate: detailItem.rate!, qty,
      photo: detailItem.photos?.[0], hostId: detailItem.hostId, brandId: detailItem.brandId,
      selectedVariants: { ...selectedVariants },
    } as any));
    closeDetail(); setQty(1); setSelectedVariants({});
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToCheckout = () => navigate(`/pay/${userId}`, { state: { items: cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/brand/${id}`); };
  const handleLogout = async () => { await dispatch(logout() as any); navigate("/AuthSwitch"); };

  /* ═══════════════════════════════════
     FILTERING — always inside Beauty
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings || []).filter((l: any) => {
      if (!l.isPublished) return false;
      if (isOutOfStock(l)) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(BEAUTY_DEPT_ID)) return false;
      if (!chain.includes(activeCat)) return false;
      if (activeSub && !chain.includes(activeSub)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const attrText = detailsOf(l).map((d) => `${d.name} ${d.value}`).join(" ");
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.vendor ?? ""} ${(l.tags ?? []).join(" ")} ${attrText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [filteredListings, activeCat, activeSub, activeDeep, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));
    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeSub ? catName(activeSub) : null;
  const catChildren = useMemo(() => childrenOf(activeCat), [activeCat]);

  /* ═══════════════════════════════════
     COUNTER RAIL (plain calls so search keeps focus)
  ═══════════════════════════════════ */
  const railRow = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: isMobile ? "11px 14px" : "13px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div className="bs-gloss" style={{ width: 30, height: 30, borderRadius: "50%", background: GLOSS, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(214,0,110,.3)" }}>
          <Sparkles size={14} color="#fff" />
        </div>
        {!isMobile && <span className="bs-serif" style={{ fontSize: 17, color: B.ink }}>Beauty</span>}
      </div>

      {locationDenied ? (
        <button className="bs-hbtn" onClick={() => setLocationModalOpen(true)} aria-label="Location off">
          <AlertTriangle size={13} />
        </button>
      ) : !userCountry ? (
        <div className="bs-hbtn">
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: HOT_PINK, animation: "bsTwinkle 1.2s infinite" }} />
          {!isMobile && <span>Locating…</span>}
        </div>
      ) : (
        <button onClick={handleToggleGlobal} className="bs-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          {!isMobile && <span>{showGlobal ? "Global" : userCity || userCountry}</span>}
        </button>
      )}

      <div className="bs-search">
        <span style={{ padding: "0 4px 0 13px", display: "flex" }}><Search size={14} color={B.faint} /></span>
        <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search product, shade or brand…" aria-label="Search beauty" />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 12px", color: B.faint, display: "flex" }}>
            <X size={13} />
          </button>
        )}
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="bs-hbtn">
        <LayoutGrid size={14} />{!isMobile && <span>Browse</span>}
      </button>

      <button onClick={goToCheckout} className="bs-hbtn" style={{ position: "relative" }}>
        <ShoppingBag size={14} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal}` : "Bag"}</span>}
        {cartTotal > 0 && (
          <span className="bs-num" style={{ position: "absolute", top: -6, right: -6, background: GLOSS, color: "#fff", fontSize: 9, fontWeight: 700, minWidth: 18, height: 18, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(214,0,110,.35)" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="bs-hbtn" aria-label="Profile"><User size={14} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                className="bs-panel"
                style={{ position: "absolute", right: 0, top: "calc(100% + 9px)", width: 194, overflow: "hidden", zIndex: 400, boxShadow: "0 16px 40px rgba(214,0,110,.14)" }}>
                {[
                  { label: "Profile", icon: <User size={13} />, go: () => navigate(`/profile/${userId}`) },
                  { label: "Orders", icon: <Package size={13} />, go: () => navigate(`/clientdashboard/${userId}`) },
                  { label: "Sign out", icon: <LogOut size={13} />, go: handleLogout },
                ].map((r, i) => (
                  <button key={r.label} onClick={r.go}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: i < 2 ? `1px solid ${B.lineSoft}` : "none", padding: "12px 15px", cursor: "pointer", fontSize: 12.5, fontWeight: 500, color: r.label === "Sign out" ? B.danger : B.inkSoft }}>
                    {r.icon}{r.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     💄 SWATCH BAR — Beauty's categories as lipstick dabs
  ═══════════════════════════════════ */
  const swatchBar = () => (
    <div className="bs-swatches">
      {BEAUTY_CATEGORIES.map((c) => {
        const on = c.id === activeCat;
        const tint = shadeFor(c.id);
        return (
          <button key={c.id} className={`bs-swatch${on ? " on" : ""}`} onClick={() => handleCatSelect(c.id)}
            title={c.name}>
            <span className="bs-dab" style={{ background: `linear-gradient(150deg, ${tint} 0%, ${tint}D9 55%, ${tint}A6 100%)` }}>
              <i />
            </span>
            <span>{c.name}</span>
          </button>
        );
      })}
    </div>
  );

  const heading = () => (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", padding: "4px 2px 16px" }}>
      <div>
        <div className="bs-caps" style={{ color: B.faint, marginBottom: 6 }}>
          Beauty · {cat?.name ?? "All"}
        </div>
        <h1 className="bs-serif" style={{ fontSize: isMobile ? 28 : 40, color: B.ink, lineHeight: 1.05 }}>
          {activeFilterName ?? cat?.name ?? "Beauty"}
        </h1>
      </div>
      <div className="bs-num" style={{ fontSize: 11.5, color: B.muted, display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
        <span><b style={{ color: B.ink }}>{visibleCount}</b> products</span>
        <span style={{ color: B.line }}>·</span>
        <span><b style={{ color: B.ink }}>{productsByBrand.length}</b> brands</span>
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     BROWSE
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const picked = activeSub === node.id || activeDeep === node.id;
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        <div className={`bs-node${picked ? " on" : ""}`}
          onClick={() => {
            if (depth === 0) handleSubSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}>
          <span style={{ display: "flex", color: picked ? HOT_PINK : B.faint, flexShrink: 0 }}>{node.icon}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
          {kids.length > 0 && (
            <motion.span animate={{ rotate: isOpen ? 90 : 0 }} style={{ display: "flex", flexShrink: 0 }}>
              <ChevronRight size={13} color={B.faint} />
            </motion.span>
          )}
          {picked && kids.length === 0 && <Check size={13} color={HOT_PINK} style={{ flexShrink: 0 }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={{ borderLeft: `1px solid ${B.line}`, marginLeft: 12, paddingLeft: 4 }}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const browsePanel = () => (
    <div className="bs-panel" style={{ padding: 15 }}>
      <div className="bs-caps" style={{ color: B.faint, marginBottom: 11 }}>{cat?.name ?? "Browse"}</div>
      {catChildren.length === 0
        ? <p style={{ fontSize: 12, color: B.faint }}>Nothing deeper here yet.</p>
        : catChildren.map((c) => <TreeNode key={c.id} node={c} depth={0} />)}
    </div>
  );

  const mobileBrowseSheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(43,15,30,.5)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", background: B.blush, borderTop: `1px solid ${B.line}`, borderRadius: "22px 22px 0 0", zIndex: 401, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: B.raise, borderRadius: 99, margin: "12px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", padding: "12px 18px 10px", borderBottom: `1px solid ${B.line}` }}>
              <span className="bs-caps" style={{ color: B.muted }}>Browse</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}>
                <X size={15} color={B.muted} />
              </button>
            </div>
            <div style={{ padding: 16 }}>{swatchBar()}<div style={{ height: 8 }} />{browsePanel()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  const subTiles = () => {
    if (!catChildren.length) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <div onClick={() => setNarrowOpen((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 12 }}>
          <Sliders size={13} color={B.muted} />
          <span className="bs-caps" style={{ color: B.muted }}>Narrow it down</span>
          <motion.span animate={{ rotate: narrowOpen ? 0 : -90 }} style={{ display: "flex" }}>
            <ChevronDown size={15} color={B.faint} />
          </motion.span>
        </div>
        <AnimatePresence initial={false}>
          {narrowOpen && (
            <motion.div key={activeCat} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3,1fr)" : "repeat(auto-fill,minmax(126px,1fr))", gap: 10 }}>
                <button className={`bs-tile${activeSub === null ? " on" : ""}`} onClick={() => { setActiveSub(null); setActiveDeep(null); }}>
                  <Droplet size={17} color={activeSub === null ? HOT_PINK : B.muted} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: activeSub === null ? B.ink : B.inkSoft }}>All</span>
                </button>
                {catChildren.map((node) => {
                  const on = activeSub === node.id;
                  return (
                    <button key={node.id} className={`bs-tile${on ? " on" : ""}`} onClick={() => handleSubSelect(node.id)}>
                      <span style={{ display: "flex", fontSize: 17, color: on ? HOT_PINK : B.muted }}>{node.icon}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: on ? B.ink : B.inkSoft, lineHeight: 1.2 }}>{node.name}</span>
                    </button>
                  );
                })}
              </div>

              {activeSub && childrenOf(activeSub).length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  {childrenOf(activeSub).map((node) => {
                    const on = activeDeep === node.id;
                    return (
                      <button key={node.id} onClick={() => handleDeepSelect(node.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 999,
                          border: `1px solid ${on ? HOT_PINK : B.line}`, background: on ? B.raise : B.panel,
                          color: on ? B.ink : B.inkSoft, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                        }}>
                        {on && <Check size={11} color={HOT_PINK} />}
                        {node.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BRAND ROW
  ═══════════════════════════════════ */
  const brandRow = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const name = partner?.name || "Brand";
    const logo = partner?.image || appLogo;
    const accent = brandAccentColor(partner);
    const city = partner?.address?.city;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1.5px solid ${accent}`, background: B.panel, padding: 0, cursor: "pointer" }}>
          <img src={logo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)} className="bs-serif"
          style={{ fontSize: 15, color: B.ink, cursor: "pointer" }}>{name}</span>
        {city && <span style={{ fontSize: 11, color: B.faint }}>{city}</span>}
        <div style={{ flex: 1, height: 1, background: B.line }} />
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 27, height: 27, borderRadius: "50%", border: `1px solid ${B.line}`, background: B.panel, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={14} color={accent} />
        </button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT CARD
  ═══════════════════════════════════ */
  const productCard = (item: any) => {
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const q = getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const chips = detailsOf(item).slice(0, 2);
    const shades = shadesOf(item);
    const configurable = hasVariants(item);
    const isWished = wished.has(item.listingId);
    const low = (item.stock ?? 0) > 0 && (item.stock ?? 0) <= 5;

    return (
      <div key={item.listingId} className="bs-card" onClick={() => openDetail(item)}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = accent;
          el.style.boxShadow = `0 14px 34px ${accent}26`;
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = B.line;
          el.style.boxShadow = "none";
        }}>
        <div className="bs-shot">
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name}
                onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.src = appLogo; el.style.opacity = "0.22"; }} />
            : <Sparkles size={32} color={B.faint} />}

          {discount && <span className="bs-flag" style={{ left: 10, background: accent, color: "#fff" }}>−{discount}%</span>}
          {low && (
            <span className="bs-flag" style={{ left: 10, top: discount ? 34 : 10, background: B.panel, color: B.warn, border: `1px solid ${B.line}` }}>
              {item.stock} left
            </span>
          )}

          <button className="bs-wish" onClick={(e) => toggleWish(item.listingId, e)} aria-label="Save">
            <Heart size={12} color={isWished ? accent : B.muted} fill={isWished ? accent : "none"} />
          </button>

          {shades.length > 0 && (
            <div style={{ position: "absolute", bottom: 10, left: 11, display: "flex", gap: 5 }}>
              {shades.slice(0, 5).map((s) => (
                <span key={s.id} className="bs-shade" title={s.name} style={{ background: s.hex || B.raise }} />
              ))}
              {shades.length > 5 && (
                <span className="bs-num" style={{ fontSize: 9.5, fontWeight: 700, color: B.muted, alignSelf: "center" }}>+{shades.length - 5}</span>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: 13, display: "flex", flexDirection: "column", gap: 7, flex: 1 }}>
          <div className="bs-caps" style={{ color: B.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {catPathLabel(item.categoryId) || item.category || "beauty"}
          </div>

          <div style={{ fontSize: 13.5, fontWeight: 500, color: B.ink, lineHeight: 1.32, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 35 }}>
            {item.name}
          </div>

          {chips.length > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {chips.map((c) => <span key={c.name} className="bs-chip">{c.name}<b>{c.value}</b></span>)}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: "auto", paddingTop: 4 }}>
            <span className="bs-serif bs-num" style={{ fontSize: 18, color: B.ink }}>{price(item.rate ?? 0)}</span>
            {discount && (
              <span className="bs-num" style={{ fontSize: 11.5, color: B.faint, textDecoration: "line-through" }}>
                {price(item.compareAtPrice ?? 0)}
              </span>
            )}
            {hasRating && (
              <span className="bs-num" style={{ marginLeft: "auto", fontSize: 10.5, color: B.muted, display: "flex", alignItems: "center", gap: 3 }}>
                <Star size={10} fill={HOT_PINK} color={HOT_PINK} />{summary.avgRating.toFixed(1)}
              </span>
            )}
          </div>

          {q > 0 && !configurable ? (
            <div className="bs-step" onClick={(e) => e.stopPropagation()} style={{ borderColor: accent }}>
              <button aria-label="Remove one" style={{ background: accent }}
                onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={13} color="#fff" />
              </button>
              <span className="bs-num" style={{ fontSize: 13.5, fontWeight: 700, color: accent }}>{q}</span>
              <button aria-label="Add one" style={{ background: accent }} onClick={(e) => handleAdd(item, e)}>
                <Plus size={13} color="#fff" />
              </button>
            </div>
          ) : (
            <button className="bs-add" onClick={(e) => handleAdd(item, e)}
              style={{ borderColor: accent, color: accent }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.background = accent; el.style.color = "#fff"; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.background = "transparent"; el.style.color = accent; }}>
              {configurable ? <Droplet size={13} /> : <Plus size={13} />}
              {configurable ? "PICK SHADE" : "ADD TO BAG"}
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     RESULTS
  ═══════════════════════════════════ */
  const emptyState = (title: string, body: string) => (
    <div style={{ textAlign: "center", padding: "64px 20px" }}>
      <div className="bs-gloss" style={{ width: 56, height: 56, borderRadius: "50%", background: B.panel2, border: `1px solid ${B.line}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Sparkles size={22} color={B.faint} />
      </div>
      <h3 className="bs-serif" style={{ fontSize: 20, color: B.ink, marginBottom: 8 }}>{title}</h3>
      <p style={{ color: B.muted, fontSize: 13, marginBottom: 22 }}>{body}</p>
      <button onClick={resetAllFilters} className="bs-hbtn" style={{ margin: "0 auto", padding: "10px 22px" }}>
        Clear filters
      </button>
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <span className="bs-caps" style={{ color: B.faint }}>Results</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort"
            style={{ background: B.panel, border: `1px solid ${B.line}`, color: B.inkSoft, borderRadius: 999, padding: "7px 13px", fontSize: 11.5, fontWeight: 500, cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal} className="bs-hbtn">
              {showGlobal ? <MapPin size={12} /> : <Globe size={12} />}
              {showGlobal ? "Local" : "Global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0
          ? emptyState(
              activeFilterName ? `No ${activeFilterName} in stock` : `Nothing in ${cat?.name ?? "this category"} yet`,
              "Try another swatch above, or clear the filters."
            )
          : locationDenied
            ? emptyState("Location is off", "Turn it on to see brands that can ship to you.")
            : emptyState(
                showGlobal ? "No global brands here" : "No local brands here",
                `Switch to ${showGlobal ? "local" : "global"} above, or clear the filters.`
              )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {brandRow(group.brandId)}
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => productCard(item))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     DETAIL
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;
    const item = detailItem;
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const brandName = partner?.name;
    const img = item.photos?.[0];
    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;

    const variantDefs = variantAttrsForItem(item);
    const details = detailsOf(item).filter((d) => !variantDefs.some((v: any) => v.name === d.name));
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const allChosen = variantDefs.every((a: any) => selectedVariants[String(a.id)] != null);
    const canContinue = !soldOut && qty > 0 && qty <= stock && allChosen;

    const content = (
      <div style={{ background: B.blushSoft, border: `1px solid ${B.line}`, borderRadius: isMobile ? "24px 24px 0 0" : 22, overflow: "hidden", width: "100%", maxWidth: isMobile ? "100%" : 470, maxHeight: isMobile ? "88vh" : "86vh", display: "flex", flexDirection: "column" }}>
        <div className="bs-gloss" style={{ height: 4, background: GLOSS, flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 99, background: B.raise, margin: "11px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "16px 20px 0" }}>
          <div style={{ position: "relative", width: "100%", aspectRatio: "1/1", borderRadius: 18, background: `radial-gradient(120% 95% at 50% 10%, ${B.panel2} 0%, ${B.panel} 72%)`, border: `1px solid ${B.line}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 15, overflow: "hidden" }}>
            {img
              ? <img src={img} alt={item.name} style={{ width: "78%", height: "78%", objectFit: "contain", filter: "drop-shadow(0 16px 24px rgba(214,0,110,.18))" }} />
              : <Sparkles size={40} color={B.faint} />}
            <button onClick={closeDetail} aria-label="Close"
              style={{ position: "absolute", top: 11, right: 11, width: 29, height: 29, borderRadius: "50%", background: "rgba(255,255,255,.94)", border: `1px solid ${B.line}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <X size={14} color={B.inkSoft} />
            </button>
            {discount && <span className="bs-flag" style={{ left: 12, background: accent, color: "#fff" }}>−{discount}%</span>}
          </div>

          {brandName && (
            <p className="bs-caps" style={{ color: accent, marginBottom: 5 }}>{brandName}</p>
          )}
          <div className="bs-serif" style={{ fontSize: 19, color: B.ink, marginBottom: 5 }}>{item.name}</div>
          {item.categoryId && (
            <p style={{ fontSize: 11, color: B.faint, marginBottom: 14 }}>{catPathLabel(item.categoryId)}</p>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
            <span className="bs-serif bs-num" style={{ fontSize: 24, color: B.ink }}>{price(item.rate ?? 0)}</span>
            {discount && <span className="bs-num" style={{ fontSize: 12.5, color: B.faint, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>}
            {hasRating && (
              <span className="bs-num" style={{ fontSize: 11.5, color: B.muted, display: "flex", alignItems: "center", gap: 4 }}>
                <Star size={11} fill={HOT_PINK} color={HOT_PINK} />
                {summary.avgRating.toFixed(1)} ({summary.totalReviews.toLocaleString()})
              </span>
            )}
          </div>

          {/* shade / option pickers */}
          {variantDefs.map((attr: any) => {
            const key = String(attr.id);
            const chosen = selectedVariants[key];
            const isColour = attr.inputType === "color";
            return (
              <div key={key} style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                  <span className="bs-caps" style={{ color: B.muted }}>{attr.name}</span>
                  {chosen == null && <span style={{ fontSize: 10, fontWeight: 700, color: HOT_PINK }}>pick one</span>}
                  {chosen != null && isColour && (
                    <span style={{ fontSize: 11, color: B.inkSoft }}>
                      {attr.options.find((o: any) => String(o.id) === String(chosen))?.name ?? ""}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: isColour ? 10 : 8 }}>
                  {attr.options.map((opt: any) => {
                    const on = String(chosen) === String(opt.id);
                    if (isColour) {
                      return (
                        <button key={opt.id} onClick={() => setSelectedVariants((p) => ({ ...p, [key]: opt.id }))}
                          title={opt.name || opt.standard} aria-label={opt.name || opt.standard}
                          style={{ width: 36, height: 36, borderRadius: "50%", cursor: "pointer", padding: 0, background: opt.hex || B.raise, border: "2px solid #fff", boxShadow: on ? `0 0 0 2px ${accent}, inset 0 -3px 5px rgba(0,0,0,.2)` : `0 0 0 1px ${B.line}, inset 0 -3px 5px rgba(0,0,0,.18)` }} />
                      );
                    }
                    return (
                      <button key={opt.id} onClick={() => setSelectedVariants((p) => ({ ...p, [key]: opt.id }))}
                        style={{
                          padding: "9px 15px", borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 600,
                          border: `1px solid ${on ? accent : B.line}`,
                          background: on ? B.raise : B.panel, color: on ? B.ink : B.inkSoft,
                        }}>
                        {opt.name || opt.standard || String(opt.id)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {details.length > 0 && (
            <div style={{ border: `1px solid ${B.line}`, borderRadius: 16, overflow: "hidden", marginBottom: 16 }}>
              <div className="bs-caps" style={{ color: B.muted, padding: "11px 14px", borderBottom: `1px solid ${B.line}`, background: B.panel2 }}>
                The details
              </div>
              {details.map((d, i) => (
                <div key={d.name} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "10px 14px", background: i % 2 ? B.panel : B.blushSoft, fontSize: 12 }}>
                  <span style={{ color: B.muted }}>{d.name}</span>
                  <span style={{ color: B.ink, textAlign: "right", fontWeight: 500 }}>{d.value}</span>
                </div>
              ))}
            </div>
          )}

          {item.vendor && (
            <p style={{ fontSize: 11.5, color: B.muted, marginBottom: 10 }}>
              <span style={{ color: B.inkSoft, fontWeight: 600 }}>Vendor:</span> {item.vendor}
            </p>
          )}
          {item.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {item.tags.map((t: string) => (
                <span key={t} className="bs-chip">#{t}</span>
              ))}
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: B.inkSoft, lineHeight: 1.75, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {soldOut ? (
            <p style={{ fontSize: 11.5, fontWeight: 700, color: B.faint, marginBottom: 12 }}>Sold out</p>
          ) : stock <= 5 ? (
            <p style={{ fontSize: 11.5, fontWeight: 700, color: HOT_PINK, marginBottom: 12 }}>Only {stock} left</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 18 }}>
              <span className="bs-caps" style={{ color: B.muted }}>Qty</span>
              <div style={{ display: "flex", border: `1px solid ${B.line}`, borderRadius: 999, overflow: "hidden", background: B.panel }}>
                <button onClick={() => setQty((v) => Math.max(1, v - 1))} aria-label="Decrease"
                  style={{ width: 38, height: 38, border: "none", background: "transparent", cursor: "pointer", color: B.inkSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={13} />
                </button>
                <div className="bs-num" style={{ width: 42, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: B.ink }}>{qty}</div>
                <button onClick={() => setQty((v) => Math.min(stock || 1, v + 1))} disabled={qty >= stock} aria-label="Increase"
                  style={{ width: 38, height: 38, border: "none", background: "transparent", cursor: qty >= stock ? "not-allowed" : "pointer", color: B.inkSoft, opacity: qty >= stock ? .4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={13} />
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "13px 20px 18px", display: "flex", gap: 9, flexShrink: 0, borderTop: `1px solid ${B.line}` }}>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 999, border: `1px solid ${canContinue ? accent : B.line}`, background: "transparent", color: canContinue ? accent : B.faint, fontWeight: 700, fontSize: 12, letterSpacing: ".06em", cursor: canContinue ? "pointer" : "not-allowed" }}>
            ADD TO BAG
          </button>
          <button className="bs-gloss" disabled={!canContinue} onClick={() => handleDetailContinue(true)}
            style={{ flex: 1.15, padding: "13px 0", borderRadius: 999, border: "none", background: canContinue ? accent : B.raise, color: canContinue ? "#fff" : B.faint, fontWeight: 700, fontSize: 12, letterSpacing: ".06em", cursor: canContinue ? "pointer" : "not-allowed" }}>
            BUY NOW
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(43,15,30,.55)", backdropFilter: "blur(5px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: .96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .96, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 470, maxWidth: "100%" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(43,15,30,.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ scale: .94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .94, y: 20 }} onClick={(e) => e.stopPropagation()}
            className="bs-panel" style={{ width: 322, padding: 24, textAlign: "center" }}>
            <div className="bs-gloss" style={{ width: 46, height: 46, borderRadius: "50%", background: GLOSS, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", boxShadow: "0 6px 18px rgba(214,0,110,.3)" }}>
              <MapPin size={19} color="#fff" />
            </div>
            <h3 className="bs-serif" style={{ fontSize: 18, color: B.ink, marginBottom: 8 }}>Location is off</h3>
            <p style={{ color: B.muted, fontSize: 12.5, lineHeight: 1.65, marginBottom: 18 }}>
              Allow location in your browser settings to see brands that can ship to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)} className="bs-hbtn" style={{ margin: "0 auto", padding: "10px 24px" }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const bottomTabs = () => (isMobile && userId ? (
    <div style={S.tabs}>
      {[
        { icon: <Home size={20} />, label: "Home", on: false, go: () => navigate(`/StoreProductsHome/${userId}`) },
        { icon: <Sparkles size={20} />, label: "Beauty", on: true, go: () => navigate("/BeautyStore") },
        {
          label: "Bag", on: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingBag size={20} />
              {cartTotal > 0 && (
                <span className="bs-num" style={{ position: "absolute", top: -5, right: -7, background: HOT_PINK, color: "#fff", fontSize: 8, fontWeight: 700, minWidth: 15, height: 15, padding: "0 3px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>
              )}
            </div>
          ), go: goToCheckout,
        },
        { icon: <Package size={20} />, label: "Orders", on: false, go: () => navigate(`/clientdashboard/${userId}`) },
        { icon: <User size={20} />, label: "Profile", on: false, go: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <button key={t.label} onClick={t.go} className={`bs-tab${t.on ? " on" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.on ? HOT_PINK : B.faint })}
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div className="bs">
      <style>{GLOBAL_CSS}</style>

      <header className="bs-top">
        {railRow()}
        <div style={{ padding: isMobile ? "0 14px" : "0 24px" }}>{!isMobile && swatchBar()}</div>
      </header>

      {mobileBrowseSheet()}

      <div style={{ maxWidth: 1320, margin: "0 auto", padding: isMobile ? "14px 14px 0" : "22px 24px 0" }}>
        {isMobile && <div style={{ marginBottom: 4 }}>{swatchBar()}</div>}
        {heading()}

        <div style={{ display: "grid", gridTemplateColumns: !isMobile && desktopCatOpen ? "252px 1fr" : "1fr", gap: 20, alignItems: "start" }}>
          {!isMobile && desktopCatOpen && (
            <aside style={{ position: "sticky", top: 150, maxHeight: "calc(100vh - 170px)", overflowY: "auto" }}>
              {browsePanel()}
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            {subTiles()}
            {renderProducts(isMobile)}
          </main>
        </div>
      </div>

      {detailModal()}
      {locationModal()}
    
    </div>
  );
};

export default BeautyStore;
